// Small planar geometry helpers for GDACS footprints. Coordinates are [lng, lat].

const key = (p) => `${p[0].toFixed(6)},${p[1].toFixed(6)}`;

const isPoint = (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]);

// Ring without repeated vertices (the closing duplicate included).
export const uniqueVertices = (ring) => {
  if (!Array.isArray(ring)) return [];
  const seen = new Set();
  const out = [];
  for (const p of ring) {
    if (!isPoint(p)) continue;
    const k = key(p);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
};

// |shoelace| / 2 in degree², only ever compared against 0.
export const ringArea = (ring) => {
  let s = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    s += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  }
  return Math.abs(s) / 2;
};

export const isValidRing = (ring) => {
  const u = uniqueVertices(ring);
  return u.length >= 3 && ringArea(u) > 0;
};

export const ringBbox = (ring) => {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const p of ring) {
    if (!isPoint(p)) continue;
    if (p[0] < minLng) minLng = p[0];
    if (p[0] > maxLng) maxLng = p[0];
    if (p[1] < minLat) minLat = p[1];
    if (p[1] > maxLat) maxLat = p[1];
  }
  return [minLng, minLat, maxLng, maxLat];
};

export const ringCentroid = (ring) => {
  const u = uniqueVertices(ring);
  if (!u.length) return null;
  return [u.reduce((a, p) => a + p[0], 0) / u.length, u.reduce((a, p) => a + p[1], 0) / u.length];
};

const shiftRing = (ring) => ring.filter(isPoint).map((p) => (p[0] < 0 ? [p[0] + 360, p[1]] : p));

// Polygon | MultiPolygon geometry -> { bbox, polys: [{ outer, holes, bbox, shifted }] } | null.
// A polygon spanning more than 180 degrees of longitude crosses the antimeridian: its
// negative longitudes are moved by +360 so one continuous shape is stored, drawn and tested.
const rawPolygons = (geometry) =>
  geometry?.type === 'Polygon' ? [geometry.coordinates] : geometry?.type === 'MultiPolygon' ? geometry.coordinates : [];

/** True when any polygon of the geometry spans more than 180 degrees of longitude. */
export function geometryCrossesAntimeridian(geometry) {
  return rawPolygons(geometry).some((rings) => isValidRing(rings?.[0]) && (([a, , b]) => b - a > 180)(ringBbox(rings[0])));
}

// forceShift: the caller found a crossing polygon elsewhere in the same event, so this one joins its frame.
export function prepareGeometry(geometry, forceShift = false) {
  const rawPolys =
    geometry?.type === 'Polygon'
      ? [geometry.coordinates]
      : geometry?.type === 'MultiPolygon'
        ? geometry.coordinates
        : [];
  const polys = [];
  for (const rings of rawPolys) {
    if (!Array.isArray(rings) || !isValidRing(rings[0])) continue;
    const [bx0, , bx1] = ringBbox(rings[0]);
    const shifted = forceShift || bx1 - bx0 > 180;
    const fix = (r) => (shifted ? shiftRing(r) : r.filter(isPoint));
    const outer = fix(rings[0]);
    const holes = rings.slice(1).filter(isValidRing).map(fix);
    polys.push({ outer, holes, bbox: ringBbox(outer), shifted });
  }
  if (!polys.length) return null;
  const boxes = polys.map((p) => p.bbox);
  return {
    polys,
    bbox: [
      Math.min(...boxes.map((b) => b[0])),
      Math.min(...boxes.map((b) => b[1])),
      Math.max(...boxes.map((b) => b[2])),
      Math.max(...boxes.map((b) => b[3])),
    ],
  };
}

// Linear interpolation between [lng, lat] points, taking the short way across the antimeridian.
export const lerpLatLng = (a, b, f) => {
  let dLng = b[0] - a[0];
  if (dLng > 180) dLng -= 360;
  else if (dLng < -180) dLng += 360;
  let lng = a[0] + dLng * f;
  if (lng > 180) lng -= 360;
  else if (lng < -180) lng += 360;
  return [lng, a[1] + (b[1] - a[1]) * f];
};
